import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, MessageCircle, Image as ImageIcon, Loader2, X } from 'lucide-react';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { canShareToStory } from '@/utils/achievementCard';
import { shareRecognition, type RecognitionFacts, type ShareChannel } from '@/utils/recognitionCard';

// ⚠️ ТРЕТИЙ ЗАХОД, 05.09.2026. Слава трижды сообщал «кнопка пропала»,
// и каждый раз причиной был ЭТОТ крестик, а не вёрстка.
//
// История: сначала он прятал блок НАВСЕГДА (localStorage = '1'), потом
// я смягчил до 30 дней. Не помогло, и понятно почему — дело не в сроке:
//   • крестик стоит вплотную к трём кнопкам, промахнуться легко;
//   • один тап гасит блок СРАЗУ В ОБОИХ местах — и под «Итого», и в тосте,
//     потому что это один компонент с одним флагом;
//   • вернуть его из интерфейса нечем: кнопки «показывать снова» нет;
//   • снаружи это неотличимо от «фича сломалась».
//
// РЕШЕНИЕ: крестик означает «не сейчас», а не «никогда». Отказ живёт
// до перезапуска приложения. Это осознанный отход от буквы правила 5
// хартии («хранится в базе»): правило написано про ГОЛОС Славы — про
// реплики, которые приложение говорит само. Здесь же не реплика, а вход
// в функцию, и отнимать функцию навсегда за один промах неправильно.
// Постоянный вход всё равно есть — «Поделиться приёмом» в Дневнике,
// там крестика нет вовсе, потому что человек приходит туда сам.
//
// ⚠️ ЧЕТВЁРТЫЙ ЗАХОД, 10.09.2026. Слава: «на компе через ТГ три кнопки
// выходят, на айфоне после фото не появляются». Проверок платформы в коде
// нет вовсе, сборка на хостах одна и та же — значит дело было в СРОКЕ отказа.
//
// Я привязал отказ к сессии (`sessionStorage`) в расчёте «до перезапуска
// приложения». На десктопе окно закрыл — сессия кончилась, блок вернулся.
// А Telegram на iPhone мини-приложение из памяти НЕ ВЫГРУЖАЕТ: сессия там
// живёт сутками. Один и тот же код, разное поведение: на компе отказ длится
// минуту, на айфоне — почти вечно. Снаружи выглядит как «на телефоне не работает».
//
// Теперь срок считается ПО ЧАСАМ, а не по жизни вкладки. Это ведёт себя
// одинаково везде и не зависит от того, как платформа обращается с webview.
const HIDE_KEY = 'share_reco_snoozed_until';
/** Отказ живёт несколько часов: «не сейчас», а не «никогда». */
const HIDE_HOURS = 4;

/**
 * Предложение поделиться разбором — под итогами распознавания.
 *
 * Почему именно здесь: все карточки достижений начинаются с 7 дней подряд,
 * а восторг у человека на первой минуте — когда он навёл камеру и получил
 * КБЖУ за секунды. Тот, кто способен привести друзей, — это он, сейчас.
 *
 * По хартии ненавязчивости: не окно, а строка; крестик убирает её на
 * несколько часов, и главное действие — кнопка «Сохранить» — не тронуто.
 */
export function ShareRecognition({ facts }: { facts: RecognitionFacts }) {
  const { profile } = useUserStore();
  const { haptic } = useTelegram();
  const [busy, setBusy] = useState<ShareChannel | null>(null);
  const [hidden, setHidden] = useState(() => {
    try { return Number(sessionStorage.getItem(HIDE_KEY) || 0) > Date.now(); } catch { return false; }
  });

  if (hidden || !profile?.id || !facts.calories) return null;

  async function share(channel: ShareChannel) {
    if (!profile?.id) return;
    haptic('light');
    setBusy(channel);
    try {
      await shareRecognition(channel, facts, {
        userId: profile.id,
        name: profile.first_name,
        refCode: profile.ref_code,
      });
    } catch (e) {
      console.error('[share recognition]', e);
    } finally {
      setBusy(null);
    }
  }

  function hide() {
    haptic('medium');
    setHidden(true);
    try { sessionStorage.setItem(HIDE_KEY, String(Date.now() + HIDE_HOURS * 3600_000)); } catch {}
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="card mt-3 p-3"
      style={{ borderColor: 'rgba(61,216,255,0.25)' }}
    >
      <div className="flex items-start gap-2 mb-2.5">
        <div className="flex-1 text-xs text-ink-muted leading-snug">
          Показать друзьям, как это считается?
        </div>
        <button onClick={hide} aria-label="Скрыть до следующего раза" className="shrink-0 -mt-1 -mr-1 w-7 h-7 flex items-center justify-center text-ink-dim">
          <X size={13} />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {canShareToStory() && (
          <Btn Icon={Send} label="В историю" busy={busy === 'story'} disabled={!!busy} onClick={() => share('story')} accent />
        )}
        <Btn Icon={MessageCircle} label="Другу" busy={busy === 'chat'} disabled={!!busy} onClick={() => share('chat')} />
        <Btn Icon={ImageIcon} label="Картинкой" busy={busy === 'image'} disabled={!!busy} onClick={() => share('image')} />
      </div>
    </motion.div>
  );
}

function Btn({ Icon, label, onClick, busy, disabled, accent }: {
  Icon: any; label: string; onClick: () => void; busy: boolean; disabled: boolean; accent?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`py-2.5 rounded-xl flex flex-col items-center gap-1 text-[11px] font-medium transition-all active:scale-[0.97] disabled:opacity-50 ${
        accent ? 'bg-sky-glow/15 border border-sky-glow/35 text-sky-glow' : 'bg-bg-elevated border border-line text-ink-muted'
      }`}
    >
      {busy ? <Loader2 size={15} className="animate-spin" /> : <Icon size={15} />}
      {label}
    </button>
  );
}
